import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import Layout from '../../components/Layout';
import { Link } from 'react-router-dom';
import { authAxios } from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import CreateSeasonWizard from './CreateSeasonWizard';
import { Upload, PlusCircle, Calculator, MessageSquare, Trash2, Pencil } from 'lucide-react';

export default function AdminDashboard() {
  const { user, currentSeason, seasons, loadSeasons, API, tenantContext } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [showCreateSeason, setShowCreateSeason] = useState(false);
  const [league, setLeague] = useState(null);
  
  // Edit episode count state
  const [showEditEpisodeCount, setShowEditEpisodeCount] = useState(false);
  const [editEpisodeCount, setEditEpisodeCount] = useState('');
  
  // Delete season state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // League options visibility (for AI Event Drafting tile gating)
  const [leagueOptionsVisible, setLeagueOptionsVisible] = useState(false);

  useEffect(() => {
    authAxios.get(`${API}/admin/league-options`)
      .then(({ data }) => setLeagueOptionsVisible(!!data?.ai_event_drafting_visible))
      .catch(() => setLeagueOptionsVisible(false));
  }, [API]);

  // Rename league state
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [newLeagueName, setNewLeagueName] = useState('');
  const [renaming, setRenaming] = useState(false);

  // Check if user can access admin features
  const tenantRole = tenantContext?.membership?.role || user?.role;
  const isLeagueAdmin = tenantRole === 'league_admin' || user?.role === 'admin';
  const isModerator = tenantRole === 'moderator';
  const canAccessAdmin = isLeagueAdmin || isModerator;

  useEffect(() => {
    if (canAccessAdmin) {
      loadLeague();
    }
  }, [user, tenantContext]);

  // Auto-open Create Season wizard for new leagues with no seasons
  useEffect(() => {
    if (league && seasons && seasons.length === 0 && isLeagueAdmin) {
      setShowCreateSeason(true);
    }
  }, [league, seasons, isLeagueAdmin]);

  const loadLeague = async () => {
    try {
      const response = await authAxios.get(`${API}/league`);
      setLeague(response.data);
    } catch (error) {
      console.error('Failed to load league:', error);
    }
  };

  const recalculateScores = async () => {
    if (!currentSeason) return;
    
    try {
      setLoading(true);
      const response = await authAxios.post(
        `${API}/seasons/${currentSeason.id}/recalculate`
      );
      toast.success(`Recalculated! ${response.data.leaderboard_count} players scored.`);
    } catch (error) {
      console.error('Failed to recalculate:', error);
      toast.error('Failed to recalculate scores');
    } finally {
      setLoading(false);
    }
  };

  const updateSeasonStatus = async (status) => {
    if (!currentSeason) return;
    
    try {
      setLoading(true);
      await authAxios.patch(
        `${API}/seasons/${currentSeason.id}`,
        { status }
      );
      toast.success(`Season status updated to ${status}`);
      loadSeasons();
    } catch (error) {
      console.error('Failed to update season:', error);
      toast.error('Failed to update season');
    } finally {
      setLoading(false);
    }
  };

  const updateEpisodeCount = async () => {
    if (!currentSeason || !editEpisodeCount) return;
    
    const newCount = parseInt(editEpisodeCount);
    if (isNaN(newCount) || newCount < 1) {
      toast.error('Please enter a valid episode count');
      return;
    }
    
    try {
      setLoading(true);
      await authAxios.patch(
        `${API}/seasons/${currentSeason.id}`,
        { episode_count: newCount }
      );
      toast.success(`Episode count updated to ${newCount}`);
      setShowEditEpisodeCount(false);
      setEditEpisodeCount('');
      loadSeasons();
    } catch (error) {
      console.error('Failed to update episode count:', error);
      toast.error(error.response?.data?.detail || 'Failed to update episode count');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleRenameClick = () => {
    setNewLeagueName(league?.name || tenantContext?.tenant?.name || '');
    setShowRenameDialog(true);
  };

  const handleRenameLeague = async () => {
    if (!newLeagueName.trim()) {
      toast.error('Please enter a league name');
      return;
    }
    
    try {
      setRenaming(true);
      await authAxios.patch(`${API}/league`, { name: newLeagueName.trim() });
      toast.success('League name updated successfully!');
      setShowRenameDialog(false);
      loadLeague(); // Reload league data
      // Reload the page to update the header
      window.location.reload();
    } catch (error) {
      console.error('Failed to rename league:', error);
      toast.error(error.response?.data?.detail || 'Failed to rename league');
    } finally {
      setRenaming(false);
    }
  };

  const handleDeleteConfirmStep1 = () => {
    setShowDeleteDialog(false);
    setShowDeleteConfirm(true);
    setDeleteConfirmText('');
  };

  const handleDeleteFinal = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    try {
      setLoading(true);
      await authAxios.delete(
        `${API}/seasons/${currentSeason.id}`
      );
      toast.success('Season permanently deleted');
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
      await loadSeasons();
    } catch (error) {
      console.error('Failed to delete season:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete season');
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  if (!canAccessAdmin) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-slate-600">You need admin privileges to access this page.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto" data-testid="admin-dashboard">
        <div className="spacing-section">
          <h1 className="heading-hero text-slate-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Admin Dashboard
          </h1>
          <p className="text-responsive text-slate-600">Manage seasons, contestants, and scoring</p>
        </div>

        {/* Season Management */}
        <div className="grid-mobile-1 lg:grid-cols-2 gap-4 md:gap-6 spacing-section">
          <Card>
            <CardHeader className="padding-card pb-2 md:pb-4">
              <CardTitle className="heading-card">Current Season</CardTitle>
            </CardHeader>
            <CardContent className="padding-card pt-0">
              {currentSeason ? (
                <div className="space-y-4">
                  {/* Season Number */}
                  <div>
                    <p className="text-xl md:text-2xl font-bold text-slate-800">Season {currentSeason.number}</p>
                  </div>
                  
                  {/* Status and Episode Count - Same Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Status Dropdown */}
                    <div>
                      <Label className="text-sm mb-2 block">Status</Label>
                      <Select
                        value={currentSeason.status}
                        onValueChange={(value) => updateSeasonStatus(value)}
                        disabled={loading}
                      >
                        <SelectTrigger className={`w-full font-medium ${
                          currentSeason.status === 'setup' ? 'bg-slate-100 text-slate-700 border-slate-300' :
                          currentSeason.status === 'drafting' ? 'bg-purple-100 text-purple-700 border-purple-300' :
                          currentSeason.status === 'live' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' :
                          currentSeason.status === 'complete' ? 'bg-blue-100 text-blue-700 border-blue-300' :
                          ''
                        }`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="setup">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-slate-500"></span>
                              Setup
                            </span>
                          </SelectItem>
                          <SelectItem value="drafting">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                              Drafting
                            </span>
                          </SelectItem>
                          <SelectItem value="live">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                              Live
                            </span>
                          </SelectItem>
                          <SelectItem value="complete">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                              Complete
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Episodes */}
                    <div>
                      <Label className="text-sm mb-2 block">Episode Count</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={currentSeason.episode_count || 13}
                          disabled
                          className="flex-1 bg-slate-50"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditEpisodeCount((currentSeason.episode_count || 13).toString());
                            setShowEditEpisodeCount(true);
                          }}
                        >
                          Edit
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-slate-600 text-sm">No season selected</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="padding-card pb-2 md:pb-4">
              <CardTitle className="heading-card">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="padding-card pt-0">
              <div className="space-y-2 md:space-y-3">
                {/* 2 buttons per row - improved mobile sizing */}
                <div className="grid grid-cols-2 gap-2 md:gap-3">
                  <Link to="/admin/import" className="w-full">
                    <Button className="w-full text-xs md:text-sm h-10 md:h-auto bg-blue-500 hover:bg-blue-600 text-white px-2 md:px-4">
                      <Upload className="w-4 h-4 mr-1 md:mr-2 flex-shrink-0" />
                      <span className="truncate">Import</span>
                    </Button>
                  </Link>
                  
                  <Button 
                    className="w-full text-xs md:text-sm h-10 md:h-auto bg-emerald-500 hover:bg-emerald-600 text-white px-2 md:px-4" 
                    onClick={() => setShowCreateSeason(true)}
                  >
                    <PlusCircle className="w-4 h-4 mr-1 md:mr-2 flex-shrink-0" />
                    <span className="truncate">New Season</span>
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-2 md:gap-3">
                  <Button
                    className="w-full text-xs md:text-sm h-10 md:h-auto bg-amber-500 hover:bg-amber-600 text-white px-2 md:px-4"
                    onClick={recalculateScores}
                    disabled={loading || !currentSeason}
                  >
                    <Calculator className="w-4 h-4 mr-1 md:mr-2 flex-shrink-0" />
                    <span className="truncate">Recalculate</span>
                  </Button>

                  <Link to="/admin/notes" className="w-full">
                    <Button 
                      className="w-full text-xs md:text-sm h-10 md:h-auto bg-pink-500 hover:bg-pink-600 text-white px-2 md:px-4"
                    >
                      <MessageSquare className="w-4 h-4 mr-1 md:mr-2 flex-shrink-0" />
                      <span className="truncate">Message</span>
                    </Button>
                  </Link>
                </div>

                <div className="grid grid-cols-2 gap-2 md:gap-3">
                  <Button
                    className="w-full text-xs md:text-sm h-10 md:h-auto bg-violet-500 hover:bg-violet-600 text-white px-2 md:px-4"
                    onClick={handleRenameClick}
                    disabled={loading}
                  >
                    <Pencil className="w-4 h-4 mr-1 md:mr-2 flex-shrink-0" />
                    <span className="truncate">Rename League</span>
                  </Button>

                  <Button
                    variant="outline"
                    onClick={handleDeleteClick}
                    disabled={loading || !currentSeason}
                    className="w-full text-xs md:text-sm h-10 md:h-auto text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300 px-2 md:px-4"
                  >
                    <Trash2 className="w-4 h-4 mr-1 md:mr-2 flex-shrink-0" />
                    <span className="truncate">Delete Season</span>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Management Links */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6">
          <Link to="/admin/players" className="block">
            <Card className="hover:shadow-lg transition-all duration-200 cursor-pointer h-full group">
              <CardContent className="p-4 md:p-6 text-center">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3 group-hover:bg-blue-200 transition-colors">
                  <svg className="w-6 h-6 md:w-7 md:h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h3 className="text-sm md:text-lg font-semibold text-slate-800 mb-1">Players</h3>
                <p className="text-xs md:text-sm text-slate-600 leading-tight">Manage users & access</p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/contestants" className="block">
            <Card className="hover:shadow-lg transition-all duration-200 cursor-pointer h-full group">
              <CardContent className="p-4 md:p-6 text-center">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3 group-hover:bg-emerald-200 transition-colors">
                  <svg className="w-6 h-6 md:w-7 md:h-7 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-sm md:text-lg font-semibold text-slate-800 mb-1">Contestants</h3>
                <p className="text-xs md:text-sm text-slate-600 leading-tight">Manage tribes & players</p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/scoring-rules" className="block">
            <Card className="hover:shadow-lg transition-all duration-200 cursor-pointer h-full group">
              <CardContent className="p-4 md:p-6 text-center">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3 group-hover:bg-purple-200 transition-colors">
                  <svg className="w-6 h-6 md:w-7 md:h-7 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h3 className="text-sm md:text-lg font-semibold text-slate-800 mb-1">Scoring</h3>
                <p className="text-xs md:text-sm text-slate-600 leading-tight">Configure point values</p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/event-log" className="block">
            <Card className="hover:shadow-lg transition-all duration-200 cursor-pointer h-full group">
              <CardContent className="p-4 md:p-6 text-center">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3 group-hover:bg-orange-200 transition-colors">
                  <svg className="w-6 h-6 md:w-7 md:h-7 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-sm md:text-lg font-semibold text-slate-800 mb-1">Event Log</h3>
                <p className="text-xs md:text-sm text-slate-600 leading-tight">Record episode events</p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/episode-calendar" className="block">
            <Card className="hover:shadow-lg transition-all duration-200 cursor-pointer h-full group">
              <CardContent className="p-4 md:p-6 text-center">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3 group-hover:bg-cyan-200 transition-colors">
                  <svg className="w-6 h-6 md:w-7 md:h-7 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-sm md:text-lg font-semibold text-slate-800 mb-1">Calendar</h3>
                <p className="text-xs md:text-sm text-slate-600 leading-tight">Episode schedule</p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/draft" className="block">
            <Card className="hover:shadow-lg transition-all duration-200 cursor-pointer h-full group">
              <CardContent className="p-4 md:p-6 text-center">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3 group-hover:bg-teal-200 transition-colors">
                  <svg className="w-6 h-6 md:w-7 md:h-7 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <h3 className="text-sm md:text-lg font-semibold text-slate-800 mb-1">Draft</h3>
                <p className="text-xs md:text-sm text-slate-600 leading-tight">Run player draft</p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/bonus-questions" className="block">
            <Card className="hover:shadow-lg transition-all duration-200 cursor-pointer h-full group">
              <CardContent className="p-4 md:p-6 text-center">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3 group-hover:bg-indigo-200 transition-colors">
                  <svg className="w-6 h-6 md:w-7 md:h-7 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-sm md:text-lg font-semibold text-slate-800 mb-1">Bonus Q&apos;s</h3>
                <p className="text-xs md:text-sm text-slate-600 leading-tight">Episode predictions</p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/league-options" className={`block ${leagueOptionsVisible ? '' : 'hidden'}`} data-testid="tile-league-options">
            <Card className="hover:shadow-lg transition-all duration-200 cursor-pointer h-full group relative">
              <span className="absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">BETA</span>
              <CardContent className="p-4 md:p-6 text-center">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3 group-hover:bg-purple-200 transition-colors">
                  <svg className="w-6 h-6 md:w-7 md:h-7 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="text-sm md:text-lg font-semibold text-slate-800 mb-1">League Options</h3>
                <p className="text-xs md:text-sm text-slate-600 leading-tight">Beta features &amp; toggles</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>

      {/* Delete Season Dialog - Step 1 */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Season {currentSeason?.number}?</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-3">
              Are you sure you want to permanently delete Season {currentSeason?.number}?
            </p>
            <p className="text-sm text-red-600 font-medium">
              Warning: This action cannot be undone.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirmStep1}>
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Season Dialog - Step 2 (Final Confirmation) */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">⚠️ Final Confirmation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800 font-medium mb-2">
                This will permanently delete Season {currentSeason?.number} and ALL related data:
              </p>
              <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                <li>All contestants and tribes</li>
                <li>All episodes and events</li>
                <li>All rosters and scoring data</li>
                <li>All bonus questions and answers</li>
                <li>All leaderboard entries</li>
              </ul>
              <p className="text-sm text-red-800 font-bold mt-3">
                This action CANNOT be undone!
              </p>
            </div>
            <div>
              <Label htmlFor="delete-confirm" className="text-sm font-medium">
                Type <span className="font-mono font-bold">DELETE</span> to confirm
              </Label>
              <Input
                id="delete-confirm"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE"
                className="font-mono mt-2"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => {
              setShowDeleteConfirm(false);
              setDeleteConfirmText('');
            }}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteFinal}
              disabled={deleteConfirmText !== 'DELETE' || loading}
            >
              {loading ? 'Deleting...' : 'Delete Season Permanently'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Episode Count Dialog */}
      <Dialog open={showEditEpisodeCount} onOpenChange={setShowEditEpisodeCount}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Episode Count</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-episode-count">Number of Episodes</Label>
              <Input
                id="edit-episode-count"
                type="number"
                value={editEpisodeCount}
                onChange={(e) => setEditEpisodeCount(e.target.value)}
                min="1"
                className="mt-2"
              />
              <p className="text-xs text-slate-500 mt-2">
                Current: {currentSeason?.episode_count || 13} episodes
              </p>
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Increasing the count will add new episodes. 
                Decreasing will remove episodes (only if they have no event logs).
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEditEpisodeCount(false);
              setEditEpisodeCount('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={updateEpisodeCount}
              disabled={loading || !editEpisodeCount}
            >
              {loading ? 'Updating...' : 'Update Episode Count'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Season Wizard */}
      <CreateSeasonWizard
        open={showCreateSeason}
        onClose={() => setShowCreateSeason(false)}
        onSuccess={() => {
          setShowCreateSeason(false);
          loadSeasons();
        }}
        league={league}
      />

      {/* Rename League Dialog */}
      <Dialog open={showRenameDialog} onOpenChange={setShowRenameDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-violet-500" />
              Rename League
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="new-league-name">League Name</Label>
              <Input
                id="new-league-name"
                value={newLeagueName}
                onChange={(e) => setNewLeagueName(e.target.value)}
                placeholder="Enter new league name"
                className="mt-2"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !renaming && newLeagueName.trim()) {
                    handleRenameLeague();
                  }
                }}
              />
            </div>
            <p className="text-xs text-slate-500">
              This will update your league name across the entire platform.
            </p>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowRenameDialog(false)}
              disabled={renaming}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRenameLeague}
              disabled={renaming || !newLeagueName.trim()}
              className="bg-violet-500 hover:bg-violet-600"
            >
              {renaming ? 'Saving...' : 'Save Name'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
